import React from 'react';
import { 
  Typography, 
  Box,
  Divider,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import PageTemplate from '../components/PageTemplate';

const PrivacyPage: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const lastUpdated = "March 15, 2024";
  
  return (
    <PageTemplate 
      title="Privacy Policy" 
      description={`Last updated: ${lastUpdated}`}
      breadcrumbs={[{ label: 'Privacy Policy', path: '/privacy' }]}
    >
      <Box sx={{ '& > h6': { mt: 4, mb: 2 } }}>
        <Typography variant="body1" paragraph>
          At 1TimeMail, we take your privacy seriously. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our temporary email service. Please read this privacy policy carefully. If you do not agree with the terms of this privacy policy, please do not access the site.
        </Typography>

        <Typography variant="h6" fontWeight={600}>
          1. INFORMATION WE COLLECT
        </Typography>
        
        <Typography variant="body1" paragraph>
          We collect information in minimal ways to provide and improve our Service to you:
        </Typography>
        
        <List>
          <ListItem>
            <ListItemText
              primary="Automatically Collected Information"
              secondary="When you use our service, we may automatically collect certain computer information such as your IP address, operating system type, browser type, referring website, pages you viewed, and the dates/times when you accessed the service. We use this information for service operation, maintenance, and improvement."
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Email Content"
              secondary="When emails are received by your temporary address, they are stored temporarily on our servers. We do not read, scan, or analyze the content of these emails for advertising or data mining purposes."
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Cookies and Web Beacons"
              secondary="We may use cookies to help customize your experience and analyze usage. You can choose to disable cookies through your browser settings, but this may affect some functionality of the service."
            />
          </ListItem>
        </List>
        
        <Typography variant="h6" fontWeight={600}>
          2. HOW WE USE YOUR INFORMATION
        </Typography>
        
        <Typography variant="body1" paragraph>
          We may use the information we collect in various ways, including to:
        </Typography>
        
        <List>
          <ListItem>
            <ListItemText primary="Provide, operate, and maintain our service" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Improve, personalize, and expand our service" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Understand and analyze how you use our service" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Develop new products, services, features, and functionality" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Prevent fraudulent activities and security breaches" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Communicate with you about service-related announcements" />
          </ListItem>
        </List>
        
        <Typography variant="h6" fontWeight={600}>
          3. DATA RETENTION AND DELETION
        </Typography>
        
        <Typography variant="body1" paragraph>
          All data associated with temporary email addresses, including received emails and attachments, is automatically deleted after:
        </Typography>
        
        <List>
          <ListItem>
            <ListItemText
              primary="Free accounts: 24 hours"
              secondary="All emails and data are automatically deleted 24 hours after creation of the temporary email address."
            />
          </ListItem>
        </List>
        
        <Typography variant="body1" paragraph>
          After the retention period, all data is permanently deleted from our servers. We do not maintain backups of expired email data.
        </Typography>
        
        <Typography variant="h6" fontWeight={600}>
          4. THIRD-PARTY DISCLOSURE
        </Typography>
        
        <Typography variant="body1" paragraph>
          We do not sell, trade, or otherwise transfer your information to outside parties except in the following cases:
        </Typography>
        
        <List>
          <ListItem>
            <ListItemText
              primary="Legal Requirements"
              secondary="We may disclose information where required by law or to protect our legal rights."
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Service Providers"
              secondary="We may share your information with trusted third parties who assist us in operating our website, conducting our business, or servicing you, as long as those parties agree to keep this information confidential."
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Advertising Partners"
              secondary="We work with Google AdSense to display advertisements. Google may use cookies to personalize ads based on your browsing behavior. No personal email data is shared with advertisers."
            />
          </ListItem>
        </List>
        
        <Typography variant="h6" fontWeight={600}>
          5. SECURITY
        </Typography>
        
        <Typography variant="body1" paragraph>
          We implement reasonable security measures to protect your personal information. However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
        </Typography>
        
        <Typography variant="body1" paragraph>
          Our security practices include:
        </Typography>
        
        <List>
          <ListItem>
            <ListItemText primary="Encryption of data in transit using SSL" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Regular security assessments and updates" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Limited access to personal information" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Physical security measures for our servers" />
          </ListItem>
        </List>
        
        <Typography variant="h6" fontWeight={600}>
          6. CHILDREN'S PRIVACY
        </Typography>
        
        <Typography variant="body1" paragraph>
          Our service is not directed to individuals under the age of 13. We do not knowingly collect personal information from children under 13. If we become aware that a child under 13 has provided us with personal information, we will take steps to delete such information. If you believe that we might have any information from or about a child under 13, please contact us.
        </Typography>
        
        <Typography variant="h6" fontWeight={600}>
          7. INTERNATIONAL USERS
        </Typography>
        
        <Typography variant="body1" paragraph>
          Our service is hosted in the United States. If you access our service from the European Union, Asia, or any other region with laws or regulations governing personal data collection, use, and disclosure that differ from United States laws, please note that you are transferring your personal data to the United States. By providing your personal data, you consent to this transfer.
        </Typography>
        
        <Typography variant="body1" paragraph>
          For users in the European Economic Area (EEA), we comply with the EU General Data Protection Regulation (GDPR). If you are in the EEA, you have certain rights regarding your personal data, including the right to access, correct, delete, or restrict processing of your personal data.
        </Typography>
        
        <Typography variant="h6" fontWeight={600}>
          8. CHANGES TO THIS PRIVACY POLICY
        </Typography>
        
        <Typography variant="body1" paragraph>
          We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date at the top. You are advised to review this Privacy Policy periodically for any changes.
        </Typography>
        
        <Typography variant="h6" fontWeight={600}>
          9. CONTACT US
        </Typography>
        
        <Typography variant="body1" paragraph>
          If you have any questions about this Privacy Policy, please contact us at:
        </Typography>
        
        <Typography variant="body1" paragraph>
          Email: privacy@1timemail.org<br />
          Address: 1TimeMail Privacy Team, 5932 Jones Way, San Francisco, CA 94108
        </Typography>
        
        <Divider sx={{ my: 4 }} />
        
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
          © {currentYear} 1TimeMail. All rights reserved.
        </Typography>
      </Box>
    </PageTemplate>
  );
};

export default PrivacyPage; 